/**
 * @file    descriptor.ts
 * @scope   SHARED
 * @summary Cross-Program Rollup subtype descriptor (plan Phase 8).
 * @consumes CPR constants/model/reducer/validation/serializer delta/mock builder
 * @produces CPR_CAPABILITIES, CprDescriptor, CPR_DESCRIPTOR
 * @sideEffects None.
 * @notes   Ships DARK behind `useDiyCrossProgramRollup` (default off). Renders through the same
 *          `DynamicTable` component as Enterprise (same component, different descriptor, §4.1).
 *
 * Drawer integration (done): `CprProvider` state, registry `DESCRIPTORS`, persistence/init/
 * validation via `useActiveEngineSlice`, trend via `useCprTrendSync`, `CprRollupSection` view,
 * preview via `engineSlices`.
 *
 * Pending work (TODO(ai)):
 *  - Merge `EnterpriseProvider` + `CprProvider` into one subtype-generic engine provider.
 *  - Program options reuse the Enterprise partition options (ENTERPRISE PLACEHOLDER).
 *  - Metric shape (`CustomCalculations`) is opaque; the view uses a raw-JSON placeholder.
 *  - Aggregation: user picks from `CPR_AGGREGATION_TYPES` (no metric→aggregation inference yet).
 *  - Trend: confirm Guardrails sends backend `TimePeriodType` names (see useCprTrendSync).
 */
import type { ChartCapabilities, ChartTypeDescriptor } from '../../engine/contracts';
import { CPR_CHART_TYPE, CPR_FEATURE_FLAG, CPR_VIEW_KEYS } from './constants';
import { CprSlice } from './model';
import { CPR_INITIAL_STATE, CprAction, cprReducer } from './state/cprReducer';
import { isCprInvalid } from './validation';
import { CPR_SERIALIZER_DELTA } from './serializerDelta';
import {
  CprMockResponse,
  CrossProgramRollupMockDataBuilder,
} from './Preview/CrossProgramRollupMockDataBuilder';

/** Widget-level settings only: every per-field / per-partition concept is off. */
export const CPR_CAPABILITIES: ChartCapabilities = {
  supportsPartitions: false,
  supportsPerFieldConfig: false,
  supportsPerFieldFilters: false,
  supportsProjectFrequency: false,
  allowNormalization: false,
  allowStatTesting: false,
  precisionEditable: false,
  allowedCalcTypes: [],
  allowedFilterTypes: [],
};

export type CprDescriptor = ChartTypeDescriptor<
  typeof CPR_CHART_TYPE,
  CprSlice,
  CprMockResponse,
  CprAction
>;

export const CPR_DESCRIPTOR: CprDescriptor = {
  id: CPR_CHART_TYPE,
  optionValue: CPR_CHART_TYPE,
  component: 'DynamicTable',
  renderBinding: { component: 'DynamicTable' },
  featureFlag: CPR_FEATURE_FLAG, // For development purposes, the feature flagg should be undefined.
  capabilities: CPR_CAPABILITIES,
  views: [
    { key: CPR_VIEW_KEYS.metric, isEntry: true },
    { key: CPR_VIEW_KEYS.managePrograms },
    { key: CPR_VIEW_KEYS.aggregation },
  ],
  hostLayout: {
    showTrend: true,
    showLegacyOptions: false,
    showLegacyFilters: false,
    mainSection: CPR_VIEW_KEYS.rollupSection,
  },
  stateSlice: {
    initialState: () => CPR_INITIAL_STATE,
    reducer: cprReducer,
  },
  validation: {
    isInvalid: (slice) => isCprInvalid(slice),
  },
  serializerDelta: CPR_SERIALIZER_DELTA,
  preview: {
    previewMode: 'mock',
    mockBuilder: (slice) => CrossProgramRollupMockDataBuilder.build(slice),
  },
};
